import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { contentApi } from 'components/content/contentApi';
import { ContentAnchor, ContentLayout } from 'components/content/models/ContentLayout';
import {
    ContentModule,
    ContentModuleType,
    EHeightOption,
    ILoyaltyBalanceModule,
    isMarketingModule
} from 'components/content/models/ContentModule';
import { ContentAnchorProperties, ContentTemplate } from 'components/content/models/ContentTemplate';
import { generateLayoutFromTemplate } from 'components/content/templates';
import {
    MESSAGE_MOBILE_CONTENT_CREATE_LAYOUT_ERROR,
    MESSAGE_MOBILE_CONTENT_CREATE_LAYOUT_SUCCESS,
    MESSAGE_MOBILE_CONTENT_DELETE_MODULE_ERROR,
    MESSAGE_MOBILE_CONTENT_DELETE_MODULE_SUCCESS
} from 'config/messages';
import { MOBILE_CONTENT_LAYOUT_CONFIG } from 'config/routes';
import { updateArrayItem } from 'lib/helpers';
import { enqueueSnackbar } from 'notistack';
import { defaultLoyaltyBalanceVisualisation } from 'components/content/forms/createModuleLoyaltyBalanceForm';
import type { AppDispatch, ApplicationState } from '../store';

export const NEW_MODULE_ID = 'NEW_MODULE_ID';

export interface ContentState {
    error: string;
    currentLayout: ContentLayout;
    isLoading: boolean;
    currentModules: ContentModule[] | null;
    selectedModuleId: string;
}

export const initialState: ContentState = {
    currentLayout: null,
    currentModules: null,
    error: null,
    isLoading: false,
    selectedModuleId: undefined
};

const createAnchorWithNewProperty = (anchor: ContentAnchor, property: Partial<ContentAnchorProperties>) => ({
    ...anchor,
    properties: {
        ...anchor.properties,
        ...property
    }
});

const contentSlice = createSlice({
    name: 'content',
    initialState,
    reducers: {
        startRequest(state) {
            state.error = null;
            state.isLoading = true;
        },
        endRequest(state) {
            state.error = null;
            state.isLoading = false;
        },
        setCurrentLayoutSuccess(state, action: PayloadAction<ContentLayout>) {
            state.error = null;
            state.currentLayout = action.payload;
        },
        setCurrentLayoutError(state, action: PayloadAction<string>) {
            state.error = action.payload;
            state.currentLayout = null;
        },
        updateCurrentLayoutProperties(state, action: PayloadAction<{ fieldName: string; value: any }>) {
            const { fieldName, value } = action.payload;
            state.error = null;
            state.currentLayout = {
                ...state.currentLayout,
                anchor: createAnchorWithNewProperty(state.currentLayout.anchor, {
                    [fieldName]: value
                } as any)
            };
        },
        addCurrentModuleSuccess(state, action: PayloadAction<ContentModule>) {
            state.error = null;
            state.currentModules = state.currentModules
                ? [...state.currentModules, action.payload]
                : [action.payload];
        },
        addCurrentModuleError(state, action: PayloadAction<string>) {
            state.error = action.payload;
            state.currentModules = [];
        },
        updateCurrentModuleSuccess(state, action: PayloadAction<{ id: string; module: ContentModule }>) {
            const { id, module } = action.payload;
            if (id && Array.isArray(state.currentModules)) {
                const moduleIndex = state.currentModules.findIndex(item => item.id === id);
                if (moduleIndex >= 0) {
                    const existingModule = state.currentModules[moduleIndex];

                    const mergedModule = {
                        ...module,
                        ...(isMarketingModule(module) &&
                        module.context === undefined &&
                        isMarketingModule(existingModule) &&
                        existingModule.context
                            ? { context: existingModule.context }
                            : {})
                    };

                    state.currentModules = updateArrayItem(state.currentModules, moduleIndex, mergedModule);
                }
            }
        },
        updateCurrentModuleProperties(
            state,
            action: PayloadAction<{ id: string; fieldName: string; value: any }>
        ) {
            const { id, fieldName, value } = action.payload;
            const index = state.currentModules.findIndex(item => id === item.id);
            const module = state.currentModules[index];
            state.error = null;

            let updatedValue = value;
            if (fieldName === 'context' && value && isMarketingModule(module) && module.context) {
                updatedValue = {
                    ...module.context,
                    ...value
                };
            }

            state.currentModules = updateArrayItem(state.currentModules, index, {
                ...module,
                [fieldName]: updatedValue
            });
        },
        updateCurrentModuleError(state, action: PayloadAction<string>) {
            state.error = action.payload;
        },
        clearCurrentModules(state) {
            state.error = null;
            state.currentModules = null;
        },
        clearCurrentLayout(state) {
            state.error = null;
            state.currentLayout = null;
        },
        updateSelectedModule(state, action: PayloadAction<string>) {
            state.selectedModuleId = action.payload;
        },
        deleteSelectedModule(state, action: PayloadAction<string>) {
            const id = action.payload;
            const moduleIds = state.currentLayout.anchor.moduleIds?.filter(item => item !== id);
            state.currentModules = state.currentModules?.filter(item => item.id !== id);
            state.selectedModuleId = undefined;
            state.currentLayout = {
                ...state.currentLayout,
                anchor: { ...state.currentLayout.anchor, moduleIds }
            };
        }
    }
});

export const {
    startRequest,
    endRequest,
    setCurrentLayoutSuccess,
    setCurrentLayoutError,
    updateCurrentLayoutProperties,
    addCurrentModuleSuccess,
    addCurrentModuleError,
    updateCurrentModuleSuccess,
    updateCurrentModuleProperties,
    updateCurrentModuleError,
    clearCurrentModules,
    clearCurrentLayout,
    updateSelectedModule,
    deleteSelectedModule
} = contentSlice.actions;

// --- Thunks ---

export const createAppLayoutFromTemplate =
    (template: ContentTemplate, push: (path: string) => void) => (dispatch: AppDispatch) => {
        dispatch(startRequest());
        dispatch(clearCurrentModules());
        const promises = Promise.all(
            template.anchor.modules.map(item => {
                const tmpItem = { ...item };
                delete tmpItem.required;
                // tmp solution, this stuff will be removed
                if (tmpItem.type === ContentModuleType.PRIMARY_ACTION) {
                    delete tmpItem.display;
                }
                if (
                    tmpItem.type !== ContentModuleType.MARKETING &&
                    tmpItem.type !== ContentModuleType.PRIMARY_ACTION
                ) {
                    delete tmpItem.items;
                }
                return contentApi.modules.create({ body: tmpItem });
            })
        );
        const moduleIds: string[] = [];
        promises
            .then(result => {
                result.forEach(item => {
                    if (!item.ok) {
                        throw new Error(item.body.message);
                    }
                    dispatch(addCurrentModuleSuccess(item.body));
                    moduleIds.push(item.body.id);
                });
                return contentApi.layouts.create({ body: generateLayoutFromTemplate(template, moduleIds) });
            })
            .then(result => {
                if (!result.ok) {
                    throw new Error(result.body.message);
                }
                dispatch(setCurrentLayoutSuccess(result.body));
                enqueueSnackbar(MESSAGE_MOBILE_CONTENT_CREATE_LAYOUT_SUCCESS, { variant: 'success' });
                push(`${MOBILE_CONTENT_LAYOUT_CONFIG.replace(/:layoutId/g, result.body.id)}`);
                dispatch(endRequest());
            })
            .catch(error => {
                dispatch(setCurrentLayoutError(error.message));
                dispatch(clearCurrentModules());
                enqueueSnackbar(MESSAGE_MOBILE_CONTENT_CREATE_LAYOUT_ERROR, { variant: 'error' });
                dispatch(endRequest());
            });
    };

export const getLayoutFromId = (layoutId: string) => async (dispatch: AppDispatch) => {
    const layoutResult = await contentApi.layouts.get(layoutId);
    if (!layoutResult.ok) {
        dispatch(setCurrentLayoutError(layoutResult.body.message));
        return;
    }
    const layout: ContentLayout = layoutResult.body;
    dispatch(setCurrentLayoutSuccess(layout));
    const result = Promise.all(layout.anchor.moduleIds.map(item => contentApi.modules.get(item)));
    await result.then(promises => {
        promises.forEach(item => {
            if (!item.ok) {
                dispatch(clearCurrentModules());
                dispatch(setCurrentLayoutError(layoutResult.body.message));
            }
            dispatch(addCurrentModuleSuccess(item.body));
        });
    });
};

export const getContentState = () => (_: never, getState: () => ApplicationState) => getState().content;

export const deleteContentModule = (id: string, layout: ContentLayout) => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    try {
        const moduleIds = layout.anchor.moduleIds?.filter(item => item !== id);
        const layoutBody = { anchor: { ...layout.anchor, moduleIds } };
        const layoutResult = await contentApi.layouts.update(layout.id, {
            body: layoutBody,
            skipResponseBody: true
        });
        if (!layoutResult.ok) {
            throw new Error(MESSAGE_MOBILE_CONTENT_DELETE_MODULE_ERROR);
        }
        const moduleResult = await contentApi.modules.delete(id, { skipResponseBody: true });
        if (!moduleResult.ok) {
            throw new Error(MESSAGE_MOBILE_CONTENT_DELETE_MODULE_ERROR);
        }
        dispatch(deleteSelectedModule(id));
        enqueueSnackbar(MESSAGE_MOBILE_CONTENT_DELETE_MODULE_SUCCESS, { variant: 'success' });
    } catch (e) {
        enqueueSnackbar(e.message, { variant: 'error' });
    }
    dispatch(endRequest());
};

export const addNewModule = (moduleType: ContentModuleType) => (dispatch: AppDispatch) => {
    let module: Partial<ContentModule> = { id: NEW_MODULE_ID, type: moduleType };
    if (moduleType !== ContentModuleType.PRIMARY_ACTION && moduleType !== ContentModuleType.LOYALTY_BALANCE) {
        module.display = { height: EHeightOption.SINGLE_HEIGHT_RECTANGLE };
    }
    if (moduleType === ContentModuleType.LOYALTY_BALANCE) {
        module = {
            ...module,
            display: { height: EHeightOption.DOUBLE_HEIGHT_RECTANGLE },
            properties: {
                visualisation: {
                    ...defaultLoyaltyBalanceVisualisation
                }
            }
        } as ILoyaltyBalanceModule;
    }
    dispatch(addCurrentModuleSuccess(module as ContentModule));
    dispatch(updateSelectedModule(NEW_MODULE_ID));
};

export default contentSlice.reducer;
